import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Typography, Box, Chip
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ORIGIN as API_URL } from "../../config/env";
import { TableRowActions, DeleteConfirmationDialog, AdminAction } from "../../components/admin";

export default function EmergencyContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState({ name: "", mobile: "", relationship: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem("rfis");

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/emergency-contacts/all`, {
        headers: { rf_token_header123_k: token },
      });
      setContacts(res.data.data);
    } catch (err) {
      toast.error("Failed to fetch contacts");
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleOpen = (contact = null) => {
    if (contact) {
      setEditContact(contact);
      setForm({ name: contact.name, mobile: contact.mobile, relationship: contact.relationship || "" });
    } else {
      setEditContact(null);
      setForm({ name: "", mobile: "", relationship: "" });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!form.name || !form.mobile) {
        toast.error("Name and mobile are required");
        return;
      }

      await axios.post(
        `${API_URL}/api/v1/emergency-contacts/default`,
        form,
        { headers: { rf_token_header123_k: token } }
      );
      toast.success("Default contact added");
      setDialogOpen(false);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save contact");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/v1/emergency-contacts/default/${deleteTarget.id}`, {
        headers: { rf_token_header123_k: token },
      });
      toast.success("Contact deleted");
      setDeleteTarget(null);
      fetchContacts();
    } catch (err) {
      toast.error("Failed to delete contact");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          SOS / Emergency Contacts
        </Typography>
        <AdminAction
          startIcon={<Add />}
          onClick={() => handleOpen()}
          disabled={contacts.length >= 2}
        >
          Add Default Contact
        </AdminAction>
      </Box>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Default emergency contacts visible to all users (max 2). Users can add up to 5 personal contacts.
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Mobile</strong></TableCell>
              <TableCell><strong>Relationship</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No default contacts configured</TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell>{contact.mobile}</TableCell>
                  <TableCell>{contact.relationship || "-"}</TableCell>
                  <TableCell>
                    <Chip label="Default" color="primary" size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <TableRowActions
                      rowId={contact.id}
                      onDelete={() => setDeleteTarget(contact)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Default Emergency Contact</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Mobile Number"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Relationship / Role"
            value={form.relationship}
            onChange={(e) => setForm({ ...form, relationship: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <AdminAction variant="outline" onClick={() => setDialogOpen(false)}>Cancel</AdminAction>
          <AdminAction onClick={handleSubmit}>Save</AdminAction>
        </DialogActions>
      </Dialog>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Contact"
        itemName={deleteTarget?.name}
        loading={deleting}
      />
    </Box>
  );
}
